import {
    isPublicationCardItem,
    Product,
    PublicationCardItemWithVideo,
    VideoQueueItemMeta,
    mapListVideo,
} from '@news-mono/web-common'
import React, { useRef, useState } from 'react'
import SwiperClass from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/react/swiper-react.js'
import { useProduct } from '../../__product'
import { BauhausVideoHubCard } from '../../cards/BauhausVideoCard/BauhausVideoCard'
import { invertMaybeLoadedItems } from '../../collections/helpers/loading'
import { Skeleton } from '../../content'
import { VideoCard } from '../../SevenNewsV2/cards/VideoCard/VideoCard'

import {
    StyledCarousel,
    StyledCarouselWrapper,
    StyledCarouselWrapperMobile,
} from './Carousel.styled'

export interface CarouselProps {
    onEvent: () => void
    currentVideo: PublicationCardItemWithVideo
    currentPath: string
    onSelect: (item: VideoQueueItemMeta) => void
    itemMeta: VideoQueueItemMeta[]
    isLoading?: boolean
    isSevenNewsV2?: boolean
}
export const Carousel: React.FC<CarouselProps> = ({
    onEvent,
    currentVideo,
    onSelect,
    itemMeta,
    isLoading,
    isSevenNewsV2,
}) => {
    const carouselWrapperVerticalRef = useRef<HTMLDivElement | null>(null)
    const swiperRef = useRef<SwiperClass>()
    const [nextVideoIndex, setNextVideoIndex] = useState(1)
    if (!itemMeta || itemMeta.length === 0) {
        return null
    }
    const items = invertMaybeLoadedItems(
        {
            loaded: true,
            result: itemMeta.map((meta) =>
                mapListVideo(meta, {
                    publicUrl: '',
                }),
            ),
        },
        11,
    )
    // onclick scroll card to top in vertical carousel
    const handleCarouselSelection = (offsetTop?: number) => {
        if (carouselWrapperVerticalRef.current) {
            if (offsetTop) {
                carouselWrapperVerticalRef.current.scrollTop = offsetTop - 3
            }
        }
    }
    const handleSwipperSelection = (itemNo?: number) => {
        if (swiperRef.current) {
            if (itemNo) {
                swiperRef.current.slideTo(itemNo)
            }
        }
    }

    if (items[0].loaded && isPublicationCardItem(items[0].result))
        return (
            <>
                <CarouselComponent isSevenNewsV2={isSevenNewsV2}>
                    <StyledCarousel
                        ref={carouselWrapperVerticalRef}
                        // fix for lazy loading images in a scrollable container
                        onScroll={() => {
                            if (
                                typeof window === 'undefined' ||
                                typeof document === 'undefined'
                            ) {
                                return
                            }
                            window.dispatchEvent(new CustomEvent('scroll'))
                        }}
                    >
                        {items.map((item, idx) => {
                            if (
                                item.loaded &&
                                isPublicationCardItem(item.result) &&
                                currentVideo
                            ) {
                                const matchingMeta = itemMeta.find(
                                    (el) => item.result.id === el.id,
                                )
                                if (matchingMeta) {
                                    return isLoading ? (
                                        <Skeleton height={120} key={idx} />
                                    ) : isSevenNewsV2 ? (
                                        <VideoCard
                                            key={idx}
                                            item={item}
                                            onEvent={onEvent}
                                            displayUpNextCopy={
                                                idx === nextVideoIndex
                                            }
                                            handleCarouselSelection={
                                                handleCarouselSelection
                                            }
                                            handleSwipperSelection={() =>
                                                handleSwipperSelection(idx)
                                            }
                                            onSelect={onSelect}
                                            itemMeta={matchingMeta}
                                            setNextVideo={() =>
                                                setNextVideoIndex(idx + 1)
                                            }
                                            index={idx}
                                        />
                                    ) : (
                                        <BauhausVideoHubCard
                                            key={idx}
                                            item={item}
                                            onEvent={onEvent}
                                            isCurrentlyPlaying={
                                                item.result.id ===
                                                currentVideo.id
                                            }
                                            handleCarouselSelection={
                                                handleCarouselSelection
                                            }
                                            handleSwipperSelection={() =>
                                                handleSwipperSelection(idx)
                                            }
                                            onSelect={onSelect}
                                            itemMeta={matchingMeta}
                                        />
                                    )
                                }
                            }
                        })}
                    </StyledCarousel>
                </CarouselComponent>

                <StyledCarouselWrapperMobile isSevenNewsV2={isSevenNewsV2}>
                    <Swiper
                        slidesPerView={'auto'}
                        slidesPerGroup={1}
                        spaceBetween={15}
                        loop={false}
                        watchOverflow={true}
                        slideToClickedSlide={true}
                        onBeforeInit={(swiper) => {
                            swiperRef.current = swiper
                        }}
                    >
                        {items.map((item, idx) => {
                            if (
                                item.loaded &&
                                isPublicationCardItem(item.result)
                            ) {
                                const matchingMeta = itemMeta.find(
                                    (el) => item.result.id === el.id,
                                )

                                if (matchingMeta) {
                                    return (
                                        <SwiperSlide
                                            key={idx}
                                            virtualIndex={idx}
                                        >
                                            {isLoading ? (
                                                <Skeleton
                                                    width={304}
                                                    height={160}
                                                    key={idx}
                                                />
                                            ) : isSevenNewsV2 ? (
                                                <VideoCard
                                                    key={idx}
                                                    item={item}
                                                    onEvent={onEvent}
                                                    displayUpNextCopy={
                                                        idx === nextVideoIndex
                                                    }
                                                    handleCarouselSelection={
                                                        handleCarouselSelection
                                                    }
                                                    handleSwipperSelection={() =>
                                                        handleSwipperSelection(
                                                            idx,
                                                        )
                                                    }
                                                    onSelect={onSelect}
                                                    itemMeta={matchingMeta}
                                                    setNextVideo={() =>
                                                        setNextVideoIndex(
                                                            idx + 1,
                                                        )
                                                    }
                                                    index={idx}
                                                />
                                            ) : (
                                                <BauhausVideoHubCard
                                                    item={item}
                                                    onEvent={onEvent}
                                                    isCurrentlyPlaying={
                                                        item.result.id ===
                                                        currentVideo.id
                                                    }
                                                    handleCarouselSelection={
                                                        handleCarouselSelection
                                                    }
                                                    handleSwipperSelection={() =>
                                                        handleSwipperSelection(
                                                            idx,
                                                        )
                                                    }
                                                    onSelect={onSelect}
                                                    itemMeta={matchingMeta}
                                                />
                                            )}
                                        </SwiperSlide>
                                    )
                                }
                            }
                        })}
                    </Swiper>
                </StyledCarouselWrapperMobile>
            </>
        )
    return null
}

const CarouselComponent: React.FC<{ isSevenNewsV2?: boolean }> = ({
    children,
    isSevenNewsV2,
}) => {
    const product = useProduct()
    if (product === Product.SevenNews) {
        // need to set Carousel in absolute positioned div, to prevent setting height of hub component based on height of carousel
        return (
            <StyledCarouselWrapper isSevenNewsV2={isSevenNewsV2}>
                {children}
            </StyledCarouselWrapper>
        )
    }
    return <>{children}</>
}
